"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.toStoredCampaign = exports.campaignKeyInputOf = exports.campaignChangeOf = exports.sameCampaignChoice = exports.isCampaignRuleField = exports.CAMPAIGN_RULE_FIELDS = exports.campaignFieldsOf = exports.CAMPAIGN_PICKED_FIELD_NAME = exports.CAMPAIGN_MAX_PICKED_FIELDS = exports.CAMPAIGN_STANDARD_FIELDS = exports.campaignSyncReads = exports.campaignChoiceIsEmpty = exports.campaignCriteriaOf = exports.campaignCriteriaOfStored = exports.isCampaignId = exports.CAMPAIGN_MAX_PICKS = exports.isCampaignWindowMonths = exports.CAMPAIGN_WINDOW_DEFAULT_MONTHS = exports.CAMPAIGN_WINDOW_MAX_MONTHS = exports.CAMPAIGN_WINDOW_MIN_MONTHS = exports.CAMPAIGN_CRITERIA_KEY = exports.SALESFORCE_CAMPAIGN_MEMBER_OBJECT = exports.SALESFORCE_CAMPAIGN_OBJECT = void 0;
const salesforceSyncCriteria_1 = require("../salesforceSyncCriteria");
/**
 * Campaign sync: the parts the server and the browser both read, so they can
 * never disagree about them (CMP0, the contracts; the design is
 * sf-v2-work/sandbox-campaigns/design.md, Part 3 and Part 6, with "Vince's
 * decisions" at the end, which are binding).
 *
 * Pure: no database, no network, no imports but the criteria helper (which has
 * none), so the browser bundles it as it is.
 *
 * Encharge keeps a READ ONLY copy of the Salesforce Campaigns the customer
 * chooses, and one row for each person who is a member of one of them (only
 * people already in Encharge, linked by Salesforce record id). They live in the
 * generic custom object store, as companies and Opportunities do: two objects,
 * `salesforce_campaign` and `salesforce_campaign_member`, one real column per
 * field the sync keeps, the 18 character Salesforce id in the built-in unique
 * `externalId`. Which Campaigns are copied is decided by the sync criteria
 * (campaignCriteriaOf), which are stored in a place of their own:
 * `account.data.serviceConfig.salesforce.syncCriteria.Campaign`.
 *
 * The existing Campaign trigger and the two Campaign steps are not touched and
 * share nothing with this (their settings, their cursor row `CampaignMember`
 * and their firing ledger stay as they are).
 */
/** The custom object that holds synced Campaigns. No dash: the segment folder type is `objects-<name>`. */
exports.SALESFORCE_CAMPAIGN_OBJECT = "salesforce_campaign";
/** The custom object that holds one row per person per Campaign. No dash, see above. */
exports.SALESFORCE_CAMPAIGN_MEMBER_OBJECT = "salesforce_campaign_member";
/** The key of the Campaign side inside the stored `syncCriteria`. */
exports.CAMPAIGN_CRITERIA_KEY = "Campaign";
// ---------------------------------------------------------------------------
// The window (S-CP2: Active, or started or ended in the last N months)
exports.CAMPAIGN_WINDOW_MIN_MONTHS = 1;
exports.CAMPAIGN_WINDOW_MAX_MONTHS = 12;
/** Vince, S-CP2: "started/ended in the last 12 months". */
exports.CAMPAIGN_WINDOW_DEFAULT_MONTHS = 12;
/** A whole number of months from 1 to 12. */
const isCampaignWindowMonths = (value) => typeof value === "number" &&
    Number.isInteger(value) &&
    value >= exports.CAMPAIGN_WINDOW_MIN_MONTHS &&
    value <= exports.CAMPAIGN_WINDOW_MAX_MONTHS;
exports.isCampaignWindowMonths = isCampaignWindowMonths;
// ---------------------------------------------------------------------------
// Which Campaigns: the criteria
/**
 * The most Campaigns a customer can pick by hand: the length of the list the
 * picker shows (the Campaign picker flows already use returns at most 2,000,
 * API triggers/triggerQueries.ts), so no Campaign the picker can show is ever
 * cut off. Picks beyond it in a stored value are left out and counted.
 */
exports.CAMPAIGN_MAX_PICKS = 2000;
/**
 * A Salesforce record id in its 18 character form, the only form the picker
 * stores. (The checksum is not checked here: that needs the API's id helper,
 * and the browser does not need it. The API turns a pick into a SOQL literal
 * through the id helper, which does check it.)
 */
const ID_18 = /^[A-Za-z0-9]{18}$/;
const isCampaignId = (value) => typeof value === "string" && ID_18.test(value);
exports.isCampaignId = isCampaignId;
const isPlainObject = (value) => typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date);
/**
 * A tick box as stored. Missing, `null` and `true` are ticked (the default is
 * on), `false` is not. Anything else is not a choice anybody made that can be
 * read: not ticked, and unreadable.
 */
const tickOf = (value) => {
    if (value === undefined || value === null || value === true) {
        return { ticked: true, unreadable: false };
    }
    return { ticked: false, unreadable: value !== false };
};
/**
 * The Campaign criteria in a stored `syncCriteria` value (the one in the
 * account's config, or a draft that is about to be saved), with the account's
 * time zone when it has one.
 */
const campaignCriteriaOfStored = (syncCriteria, timezone) => {
    const side = isPlainObject(syncCriteria)
        ? syncCriteria[exports.CAMPAIGN_CRITERIA_KEY]
        : undefined;
    const stored = isPlainObject(side) ? side : {};
    // a window that is missing, or null, was never chosen: the default, no warning
    const hasWindow = stored.windowMonths !== undefined && stored.windowMonths !== null;
    const windowReadable = (0, exports.isCampaignWindowMonths)(stored.windowMonths);
    // only the `rules` key is read for a Campaign
    const reading = (0, salesforceSyncCriteria_1.readSideOf)(stored.rules === undefined || stored.rules === null
        ? undefined
        : { rules: stored.rules });
    // picks: valid 18 character ids, each once, at most the most
    const rawPicks = Array.isArray(stored.picks) ? stored.picks : [];
    const seen = new Set();
    const picks = [];
    let unreadable = 0;
    let cutOff = 0;
    rawPicks.forEach((pick) => {
        if (!(0, exports.isCampaignId)(pick) || seen.has(pick)) {
            unreadable += 1;
            return;
        }
        seen.add(pick);
        if (picks.length >= exports.CAMPAIGN_MAX_PICKS) {
            cutOff += 1;
            return;
        }
        picks.push(pick);
    });
    const active = tickOf(stored.includeActive);
    const recent = tickOf(stored.includeRecent);
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ off: stored.off !== false, windowMonths: windowReadable
            ? stored.windowMonths
            : exports.CAMPAIGN_WINDOW_DEFAULT_MONTHS, includeActive: active.ticked, includeRecent: recent.ticked, picks, groups: reading.groups, keepOut: reading.keepOut }, (typeof timezone === "string" && timezone !== "" ? { timezone } : {})), (reading.refused ? { refused: reading.refused } : {})), (hasWindow && !windowReadable ? { windowUnreadable: true } : {})), (unreadable > 0 ? { picksUnreadable: unreadable } : {})), (cutOff > 0 ? { picksCutOff: cutOff } : {})), (active.unreadable || recent.unreadable
        ? { choiceUnreadable: true }
        : {}));
};
exports.campaignCriteriaOfStored = campaignCriteriaOfStored;
/**
 * The Campaign criteria of an account, from `account.data` (the object the
 * account row holds: `serviceConfig.salesforce.syncCriteria` and `timezone`).
 */
const campaignCriteriaOf = (accountData) => {
    const data = isPlainObject(accountData) ? accountData : {};
    const salesforce = isPlainObject(data.serviceConfig)
        ? data.serviceConfig.salesforce
        : undefined;
    return (0, exports.campaignCriteriaOfStored)(isPlainObject(salesforce) ? salesforce.syncCriteria : undefined, data.timezone);
};
exports.campaignCriteriaOf = campaignCriteriaOf;
/**
 * Has the customer chosen anything to read? A pick, or one of the two tick
 * boxes. When the switch is on and this is false the page says "Choose the
 * Campaigns to copy." and nothing is read (Screen 1).
 */
const campaignChoiceIsEmpty = (criteria) => !criteria.includeActive &&
    !criteria.includeRecent &&
    criteria.picks.length === 0;
exports.campaignChoiceIsEmpty = campaignChoiceIsEmpty;
/**
 * Does the Campaign sync read anything for these criteria? It is switched on
 * (`off` is false) and something is chosen. The stored rules being unreadable
 * (`refused`) and a tick box nobody can read (`choiceUnreadable`) are not looked
 * at here: the poller stops with the reason.
 */
const campaignSyncReads = (criteria) => !criteria.off && !(0, exports.campaignChoiceIsEmpty)(criteria);
exports.campaignSyncReads = campaignSyncReads;
// ---------------------------------------------------------------------------
// Which fields: the standard set
/**
 * The Campaign fields the sync always reads and keeps (locked on the mapping
 * screen as "Always synced"), by Salesforce API name, in the order the store
 * lists them. `Id` is also always read: it is the row's key (`externalId`), so
 * it has no column of its own. `IsDeleted` is how a deletion in Salesforce
 * reaches Encharge: the Campaign is KEPT with this true and never updated again
 * (CR15, S-CP6); it is read through queryAll, the only read that returns a
 * deleted row. The Campaign's members and the other facts the member rows copy
 * (its path in the hierarchy, its statuses) are kept by the sync itself and are
 * not Salesforce fields.
 */
exports.CAMPAIGN_STANDARD_FIELDS = [
    "Name",
    "Type",
    "Status",
    "IsActive",
    "StartDate",
    "EndDate",
    "ParentId",
    "OwnerId",
    "IsDeleted",
    "SystemModstamp",
];
/** Fields picked beyond the standard set: at most this many (CE29, as Opportunities). */
exports.CAMPAIGN_MAX_PICKED_FIELDS = 200;
/**
 * A Salesforce field API name that can be picked: letters, digits and
 * underscores, 80 characters at most, no dots (the mapping's own rule).
 */
exports.CAMPAIGN_PICKED_FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]{0,79}$/;
const STANDARD_LOWER = new Set(exports.CAMPAIGN_STANDARD_FIELDS.map((name) => name.toLowerCase()));
/** The Campaign fields a mapping asks for. Never throws. */
const campaignFieldsOf = (mapping) => {
    const node = isPlainObject(mapping) && isPlainObject(mapping.properties)
        ? mapping.properties[exports.CAMPAIGN_CRITERIA_KEY]
        : undefined;
    const fields = isPlainObject(node) && isPlainObject(node.properties)
        ? node.properties
        : {};
    const seen = new Set();
    const valid = [];
    Object.keys(fields).forEach((name) => {
        const entry = fields[name];
        if (!isPlainObject(entry))
            return;
        if (!exports.CAMPAIGN_PICKED_FIELD_NAME.test(name))
            return;
        const sync = entry["x-encharge-sync"];
        if (isPlainObject(sync) && sync.direction === "none")
            return;
        const lower = name.toLowerCase();
        if (STANDARD_LOWER.has(lower) || seen.has(lower))
            return;
        seen.add(lower);
        valid.push(name);
    });
    return {
        standard: exports.CAMPAIGN_STANDARD_FIELDS,
        picked: valid.slice(0, exports.CAMPAIGN_MAX_PICKED_FIELDS),
        cutOff: Math.max(0, valid.length - exports.CAMPAIGN_MAX_PICKED_FIELDS),
    };
};
exports.campaignFieldsOf = campaignFieldsOf;
// ---------------------------------------------------------------------------
// The fields a rule may use (CE22)
/**
 * The Campaign fields a sync rule may use, by Salesforce API name: Name, Type,
 * Status, Active, and the two dates (design CE22). They are all in the sync's own
 * picker query, so a rule is judged on the list the customer chooses from, before
 * saving, without reading any member. Another field (a custom one) is not offered
 * and a rule that names one is refused at the save check by the field list the
 * screen draws, not here (the loader judges any name it is given).
 */
exports.CAMPAIGN_RULE_FIELDS = [
    "Name",
    "Type",
    "Status",
    "IsActive",
    "StartDate",
    "EndDate",
];
const isCampaignRuleField = (name) => typeof name === "string" &&
    exports.CAMPAIGN_RULE_FIELDS.some((field) => field.toLowerCase() === name.toLowerCase());
exports.isCampaignRuleField = isCampaignRuleField;
const rulesOf = (criteria) => (Object.assign({ groups: criteria.groups, keepOut: criteria.keepOut }, (criteria.refused ? { refused: criteria.refused } : {})));
/** Whether two criteria choose the same Campaigns: the same switch, window, boxes, picks (as a set) and rules (sameMatching). */
const sameCampaignChoice = (a, b) => a.off === b.off &&
    a.windowMonths === b.windowMonths &&
    a.includeActive === b.includeActive &&
    a.includeRecent === b.includeRecent &&
    a.picks.length === b.picks.length &&
    a.picks.every((pick) => b.picks.includes(pick)) &&
    (0, salesforceSyncCriteria_1.sameMatching)(rulesOf(a), rulesOf(b));
exports.sameCampaignChoice = sameCampaignChoice;
const campaignChangeOf = (stored, next) => {
    const before = (0, exports.campaignCriteriaOfStored)(stored);
    const after = (0, exports.campaignCriteriaOfStored)(next);
    const same = (0, salesforceSyncCriteria_1.sameMatching)(rulesOf(before), rulesOf(after));
    const rules = same
        ? "same"
        : (0, salesforceSyncCriteria_1.rulesAreOnlyTighter)(rulesOf(after), rulesOf(before))
            ? "tighter"
            : "other";
    const picksAdded = after.picks.filter((pick) => !before.picks.includes(pick));
    const wasReading = (0, exports.campaignSyncReads)(before);
    const reads = (0, exports.campaignSyncReads)(after);
    const switchedOn = before.off && !after.off;
    const common = {
        changed: !(0, exports.sameCampaignChoice)(before, after),
        switchedOn,
        switchedOff: !before.off && after.off,
        windowFrom: before.windowMonths,
        windowTo: after.windowMonths,
        widened: after.windowMonths > before.windowMonths,
        shortened: after.windowMonths < before.windowMonths,
        rules,
        picksAdded,
    };
    if (!reads)
        return Object.assign(Object.assign({}, common), { readsMore: false });
    if (!wasReading) {
        // nothing was read under the old choice: all of it is new
        return Object.assign(Object.assign({}, common), { readsMore: true, pass: "choice" });
    }
    const grew = (!before.includeActive && after.includeActive) ||
        (!before.includeRecent && after.includeRecent) ||
        picksAdded.length > 0;
    if (grew)
        return Object.assign(Object.assign({}, common), { readsMore: true, pass: "choice" });
    if (rules === "other")
        return Object.assign(Object.assign({}, common), { readsMore: true, pass: "rules" });
    // a longer window adds Campaigns only while the "recent" box is ticked
    if (common.widened && after.includeRecent && before.includeRecent) {
        return Object.assign(Object.assign({}, common), { readsMore: true, pass: "widen" });
    }
    return Object.assign(Object.assign({}, common), { readsMore: false });
};
exports.campaignChangeOf = campaignChangeOf;
/**
 * What goes into the fingerprint of the Campaign side (the API's criteriaKeyOfStored
 * hashes it with Lead, Contact and Opportunity): undefined for a side that is not
 * an object or holds none of its keys, so an account that never chose anything for
 * Campaigns has the fingerprint it had before this side existed. Otherwise the keys
 * in a fixed order, `rules` as stored. Never throws.
 */
const campaignKeyInputOf = (storedSide) => {
    if (!isPlainObject(storedSide))
        return undefined;
    const has = (key) => storedSide[key] !== undefined && storedSide[key] !== null;
    if (!salesforceSyncCriteria_1.CAMPAIGN_SIDE_KEYS.some(has))
        return undefined;
    const out = {};
    salesforceSyncCriteria_1.CAMPAIGN_SIDE_KEYS.forEach((key) => {
        if (has(key))
            out[key] = storedSide[key];
    });
    return out;
};
exports.campaignKeyInputOf = campaignKeyInputOf;
const storedRulesOf = (draft) => ({
    version: 1,
    include: {
        type: "group",
        operator: "or",
        conditions: draft.groups.map((group) => ({
            type: "group",
            operator: "and",
            conditions: group.map(salesforceSyncCriteria_1.ruleToStored),
        })),
    },
    exclude: {
        type: "group",
        operator: "or",
        conditions: draft.keepOut.map(salesforceSyncCriteria_1.ruleToStored),
    },
});
/**
 * What the screen writes for the Campaign side (the counterpart of
 * toStoredOpportunity):
 *  - the draft chooses what `storedBefore` chooses (sameCampaignChoice): the side
 *    exactly as it loaded, byte for byte;
 *  - else `{ off, windowMonths?, includeActive?, includeRecent?, picks?, rules? }`:
 *    `off: false` while the sync is on and `off: true` while it is switched off
 *    again (the choice is kept for the next time); a tick box only when it is
 *    unticked (`false`, the default being ticked); the window when the stored side
 *    had one or it is not 12; `picks` when there is one; `rules` (always the
 *    grouped shape) only when there is a rule. A side with nothing to say is `{}`.
 * `storedBefore` is the whole `syncCriteria` value the draft was read from (or the
 * rules being restored).
 */
const toStoredCampaign = (draft, storedBefore) => {
    const base = isPlainObject(storedBefore) && isPlainObject(storedBefore[salesforceSyncCriteria_1.CAMPAIGN_SIDE])
        ? storedBefore[salesforceSyncCriteria_1.CAMPAIGN_SIDE]
        : undefined;
    const hadOff = base !== undefined && base.off !== undefined;
    const candidate = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (!draft.off ? { off: false } : hadOff ? { off: true } : {})), ((base === null || base === void 0 ? void 0 : base.windowMonths) !== undefined ||
        draft.windowMonths !== exports.CAMPAIGN_WINDOW_DEFAULT_MONTHS
        ? { windowMonths: draft.windowMonths }
        : {})), (!draft.includeActive ? { includeActive: false } : {})), (!draft.includeRecent ? { includeRecent: false } : {})), (draft.picks.length > 0 ? { picks: [...draft.picks] } : {})), (draft.groups.length > 0 || draft.keepOut.length > 0
        ? { rules: storedRulesOf(draft) }
        : {}));
    if (base !== undefined &&
        (0, exports.sameCampaignChoice)((0, exports.campaignCriteriaOfStored)({ [salesforceSyncCriteria_1.CAMPAIGN_SIDE]: base }), (0, exports.campaignCriteriaOfStored)({ [salesforceSyncCriteria_1.CAMPAIGN_SIDE]: candidate }))) {
        return base;
    }
    return candidate;
};
exports.toStoredCampaign = toStoredCampaign;
//# sourceMappingURL=campaignSync.js.map